<template>
  <div class="wrapper">
    <v-progress-circular v-if="loading" indeterminate class="d-block mx-auto mt-8"></v-progress-circular>

    <v-card v-else class="card-form mb-8">
      <v-card-title class="headline background-gradient">
        <span class="headline ml-3">Translations</span>
      </v-card-title>

      <v-card-text class="pt-2 px-6">
        <div class="d-flex flex-nowrap align-end toolbar-row">
          <div class="d-flex flex-nowrap align-end filters-row">
            <v-select
              v-model="filters.lang"
              :items="langOptions"
              item-text="label"
              item-value="value"
              label="Language"
              outlined
              dense
              hide-details
              class="filter-select mr-2"
              @change="onFilterChange"
            ></v-select>
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              item-text="label"
              item-value="value"
              label="Status"
              outlined
              dense
              hide-details
              class="filter-select mr-2"
              @change="onFilterChange"
            ></v-select>
            <v-select
              v-model="filters.source"
              :items="sourceOptions"
              item-text="label"
              item-value="value"
              label="Source"
              outlined
              dense
              hide-details
              class="filter-select mr-2"
              @change="onFilterChange"
            ></v-select>
            <v-select
              v-model="filters.type"
              :items="typeOptions"
              item-text="label"
              item-value="value"
              label="Type"
              outlined
              dense
              hide-details
              class="filter-select"
              @change="onFilterChange"
            ></v-select>
          </div>
          <div class="d-flex flex-nowrap align-center counters-row">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip class="counter-chip" outlined v-bind="attrs" v-on="on">
                  <v-icon left x-small>mdi-clock-outline</v-icon>
                  {{ counterStatus('pending') }}
                </v-chip>
              </template>
              <span>Traducciones pendientes de revisar</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip class="counter-chip" outlined v-bind="attrs" v-on="on">
                  <v-icon left x-small color="#52ae32">mdi-check-circle</v-icon>
                  {{ counterStatus('approved') }}
                </v-chip>
              </template>
              <span>Traducciones aprobadas</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip class="counter-chip" outlined v-bind="attrs" v-on="on">
                  <v-icon left x-small color="#c62828">mdi-close-circle</v-icon>
                  {{ counterStatus('rejected') }}
                </v-chip>
              </template>
              <span>Traducciones rechazadas</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  class="counter-chip"
                  color="#047ec3"
                  text-color="white"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left x-small>mdi-earth</v-icon>
                  {{ counterSource('ESCO_API') }}
                </v-chip>
              </template>
              <span>Traducciones del API europeo ESCO</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  class="counter-chip"
                  color="#52ae32"
                  text-color="white"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left x-small>mdi-robot</v-icon>
                  {{ counterSource('AI') }}
                </v-chip>
              </template>
              <span>Traducciones hechas por IA</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  class="counter-chip"
                  outlined
                  color="#047ec3"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left x-small>mdi-account</v-icon>
                  {{ counterSource('HUMAN') }}
                </v-chip>
              </template>
              <span>Traducciones hechas por una persona</span>
            </v-tooltip>
          </div>
        </div>

        <v-alert v-if="notice" :type="noticeType" text class="mt-3 mb-0">{{ notice }}</v-alert>

        <v-simple-table class="translations-table mt-2">
          <thead>
            <tr>
              <th class="uri-cell">ESCO</th>
              <th class="type-cell">Type</th>
              <th class="lang-cell">Lang</th>
              <th class="text-cell">Source text</th>
              <th class="proposed-cell">text</th>
              <th class="source-cell">Source</th>
              <th class="status-cell">Status</th>
              <th class="actions-cell"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="items.length === 0">
              <td colspan="8" class="text-center py-6">No rows for these filters.</td>
            </tr>
            <tr v-for="item in items" :key="item.id">
              <td class="uri-cell">
                <a
                  v-if="escoApiHref(item)"
                  :href="escoApiHref(item)"
                  class="esco-link"
                  @click="openEscoApi(item, $event)"
                >
                  ESCO Link
                </a>
                <span v-else>—</span>
              </td>
              <td class="type-cell">{{ item.concept_type || '—' }}</td>
              <td class="lang-cell">{{ item.target_lang }}</td>
              <td class="text-cell">{{ item.source_text }}</td>
              <td class="proposed-cell">{{ item.proposed_text }}</td>
              <td class="source-cell">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      small
                      :color="sourceIconColor(item.source)"
                    >
                      {{ sourceIcon(item.source) }}
                    </v-icon>
                  </template>
                  <span>{{ sourceHint(item.source) }}</span>
                </v-tooltip>
              </td>
              <td class="status-cell">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      small
                      :color="statusIconColor(item.status)"
                    >
                      {{ statusIcon(item.status) }}
                    </v-icon>
                  </template>
                  <span>{{ statusHint(item.status) }}</span>
                </v-tooltip>
              </td>
              <td class="actions-cell">
                <v-btn
                  icon
                  small
                  color="#047ec3"
                  :disabled="busyId === item.id"
                  @click="openEdit(item)"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <div class="d-flex align-center justify-space-between mt-4 px-2">
          <v-btn outlined color="#047ec3" :disabled="!canPrev || loadingPage" @click="prevPage">
            Previous
          </v-btn>
          <span class="text-body-2">Page {{ page }} · {{ total }} total</span>
          <v-btn outlined color="#047ec3" :disabled="!canNext || loadingPage" @click="nextPage">
            Next
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="editOpen" max-width="980" scrollable>
      <v-card v-if="editFocus">
        <v-card-title class="headline background-gradient">
          <span class="headline ml-3">Edit translations</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="editOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-6">
          <p class="text-body-2 mb-1">
            <strong>Type:</strong> {{ editFocus.concept_type || '—' }}
            <span class="mx-2">·</span>
            <strong>EN:</strong> {{ editFocus.source_text }}
          </p>
          <p class="text-body-2 mb-4">
            <a
              v-if="escoApiHref(editFocus)"
              :href="escoApiHref(editFocus)"
              class="esco-link"
              @click="openEscoApi(editFocus, $event)"
            >ESCO API</a>
          </p>
          <v-alert v-if="notice && editOpen" :type="noticeType" text dense class="mb-3">{{ notice }}</v-alert>
          <v-progress-linear v-if="editLoading" indeterminate color="#047ec3" class="mb-4"></v-progress-linear>
          <v-simple-table v-else class="translations-table translations-table--dialog">
            <thead>
              <tr>
                <th class="lang-cell">Lang</th>
                <th class="proposed-cell">text</th>
                <th class="source-cell">Source</th>
                <th class="status-cell">Status</th>
                <th class="actions-cell"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="editRows.length === 0">
                <td colspan="5" class="text-center py-6">No translations for this concept.</td>
              </tr>
              <tr v-for="(row, index) in editRows" :key="row.id">
                <td class="lang-cell">{{ row.target_lang }}</td>
                <td class="proposed-cell">
                  <v-textarea
                    v-if="isDraft(row) || (index > 0 && isEditable(row))"
                    v-model="row.proposed_text"
                    rows="2"
                    auto-grow
                    outlined
                    dense
                    hide-details
                    @input="onDraftTextInput(row)"
                  ></v-textarea>
                  <span v-else>{{ row.proposed_text }}</span>
                </td>
                <td class="source-cell">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        small
                        :color="sourceIconColor(row.source)"
                      >
                        {{ sourceIcon(row.source) }}
                      </v-icon>
                    </template>
                    <span>{{ sourceHint(row.source) }}</span>
                  </v-tooltip>
                </td>
                <td class="status-cell">
                  <span v-if="isDraft(row)">—</span>
                  <v-tooltip v-else bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        small
                        :color="statusIconColor(row.status)"
                      >
                        {{ statusIcon(row.status) }}
                      </v-icon>
                    </template>
                    <span>{{ statusHint(row.status) }}</span>
                  </v-tooltip>
                </td>
                <td class="actions-cell">
                  <template v-if="isDraft(row)">
                    <v-btn
                      x-small
                      class="button-gradient-half mr-1 mb-1"
                      depressed
                      :loading="busyId === row.id"
                      :disabled="busyId === row.id"
                      @click="saveDraft(row)"
                    >
                      Save
                    </v-btn>
                    <v-btn
                      x-small
                      outlined
                      color="#c62828"
                      class="mb-1"
                      :disabled="busyId === row.id"
                      @click="removeDraft(row)"
                    >
                      Delete
                    </v-btn>
                  </template>
                  <template v-else-if="index > 0 && canAct(row)">
                    <v-btn
                      x-small
                      class="button-gradient-half mr-1 mb-1"
                      depressed
                      :disabled="busyId === row.id"
                      @click="confirmItem(row)"
                    >
                      Confirm
                    </v-btn>
                    <v-btn
                      x-small
                      outlined
                      color="#047ec3"
                      class="mr-1 mb-1"
                      :disabled="busyId === row.id"
                      @click="rejectItem(row)"
                    >
                      Reject
                    </v-btn>
                  </template>
                  <span v-else class="text-caption">read-only</span>
                  <v-tooltip v-if="!isDraft(row)" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span class="d-inline-block" v-bind="attrs" v-on="on">
                        <v-btn
                          icon
                          x-small
                          class="ml-1"
                          color="#c62828"
                          :disabled="!canDeleteTranslation(row, index) || busyId === row.id"
                          @click="deleteItem(row)"
                        >
                          <v-icon x-small>mdi-delete</v-icon>
                        </v-btn>
                      </span>
                    </template>
                    <span>{{ canDeleteTranslation(row, index) ? 'Delete translation' : 'English cannot be deleted' }}</span>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <div class="d-flex flex-nowrap align-center justify-center mt-4 add-row-bar">
            <v-select
              v-model="addLang"
              :items="addLangOptions"
              item-text="label"
              item-value="value"
              label="Language"
              outlined
              dense
              hide-details
              class="filter-select mr-3"
            ></v-select>
            <v-btn
              small
              depressed
              class="button-gradient-half mr-2"
              :disabled="!!addBusy"
              @click="addManualEntry"
            >
              Add manual entry
            </v-btn>
            <v-btn
              small
              outlined
              color="#047ec3"
              class="mr-2"
              :loading="addBusy === 'AI'"
              :disabled="!!addBusy"
              @click="addAiEntry"
            >
              Add AI translation
            </v-btn>
            <v-btn
              small
              outlined
              color="#047ec3"
              :loading="addBusy === 'ESCO'"
              :disabled="!!addBusy"
              @click="addEscoEntry"
            >
              Add ESCO API Translation
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'BackofficeTranslations',
  data: () => ({
    loading: true,
    loadingPage: false,
    busyId: null,
    items: [],
    total: 0,
    limit: 50,
    offset: 0,
    counters: {
      bySource: { ESCO_API: 0, AI: 0, HUMAN: 0 },
      byStatus: { pending: 0, approved: 0, rejected: 0 }
    },
    filters: {
      lang: 'en',
      status: 'all',
      source: 'all',
      type: 'all'
    },
    langOptions: [
      { label: 'EN', value: 'en' },
      { label: 'ES', value: 'es' },
      { label: 'IT', value: 'it' },
      { label: 'DE', value: 'de' }
    ],
    statusOptions: [
      { label: 'pending', value: 'pending' },
      { label: 'approved', value: 'approved' },
      { label: 'rejected', value: 'rejected' },
      { label: 'all', value: 'all' }
    ],
    sourceOptions: [
      { label: 'ESCO_API', value: 'ESCO_API' },
      { label: 'AI', value: 'AI' },
      { label: 'HUMAN', value: 'HUMAN' },
      { label: 'all', value: 'all' }
    ],
    typeOptions: [
      { label: 'all', value: 'all' },
      { label: 'occupation', value: 'occupation' },
      { label: 'skill', value: 'skill' },
      { label: 'knowledge', value: 'knowledge' },
      { label: 'isco', value: 'isco' }
    ],
    notice: '',
    noticeType: 'success',
    editOpen: false,
    editLoading: false,
    editFocus: null,
    editRows: [],
    addLang: 'es',
    addBusy: ''
  }),
  computed: {
    page() {
      return Math.floor(this.offset / this.limit) + 1
    },
    canPrev() {
      return this.offset > 0
    },
    canNext() {
      return this.offset + this.limit < this.total
    },
    addLangOptions() {
      return this.langOptions.filter((item) => item.value !== 'en')
    }
  },
  async mounted() {
    await this.load(true)
  },
  methods: {
    counterSource(key) {
      const bySource = this.counters && this.counters.bySource
      return bySource && bySource[key] != null ? bySource[key] : 0
    },
    counterStatus(key) {
      const byStatus = this.counters && this.counters.byStatus
      return byStatus && byStatus[key] != null ? byStatus[key] : 0
    },
    conceptUri(uri) {
      const text = String(uri || '').trim()
      if (!text) return ''
      if (/^https?:\/\//i.test(text)) return text
      if (text.indexOf('esco/') === 0) return 'http://data.europa.eu/' + text
      if (text.indexOf('/esco/') >= 0) {
        return 'http://data.europa.eu' + text.slice(text.indexOf('/esco/'))
      }
      return text
    },
    escoApiHref(item) {
      const uri = this.conceptUri(item && item.uri)
      if (!uri) return ''
      let resource = 'skill'
      if (uri.indexOf('/esco/occupation/') >= 0 || uri.indexOf('/esco/isco/') >= 0) {
        resource = 'occupation'
      }
      return (
        'https://ec.europa.eu/esco/api/resource/' +
        resource +
        '?language=en&selectedVersion=v1.2.1&uri=' +
        encodeURIComponent(uri)
      )
    },
    openEscoApi(item, event) {
      const href = this.escoApiHref(item)
      if (!href) return
      if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return
      event.preventDefault()
      const width = Math.min(1100, Math.round(window.screen.availWidth * 0.8) || 900)
      const height = Math.min(800, Math.round(window.screen.availHeight * 0.8) || 700)
      window.open(
        href,
        'esco-api-' + (item && item.id ? item.id : Date.now()),
        'noopener,noreferrer,width=' +
          width +
          ',height=' +
          height +
          ',menubar=no,toolbar=yes,scrollbars=yes,resizable=yes'
      )
    },
    sortEditRows(rows) {
      const order = { en: 0, es: 1, it: 2, de: 3 }
      return (rows || []).slice().sort((a, b) => {
        const left = order[a.target_lang] != null ? order[a.target_lang] : 9
        const right = order[b.target_lang] != null ? order[b.target_lang] : 9
        if (left !== right) return left - right
        return String(a.id).localeCompare(String(b.id))
      })
    },
    async openEdit(item) {
      this.editFocus = item
      this.editRows = []
      this.addBusy = ''
      this.editOpen = true
      await this.loadEditRows()
    },
    async loadEditRows() {
      const uri = this.editFocus && this.editFocus.uri
      if (!uri) {
        this.editRows = []
        return
      }
      this.editLoading = true
      try {
        const drafts = (this.editRows || []).filter((row) => row.isDraft)
        const params = new URLSearchParams()
        params.set('uri', uri)
        params.set('limit', '50')
        params.set('offset', '0')
        const result = await this.api('backoffice/translations?' + params.toString())
        const rows = this.sortEditRows(result.items || []).map((row) => Object.assign({}, row))
        const hasEn = rows.some((row) => row.target_lang === 'en')
        if (!hasEn && this.editFocus && this.editFocus.source_text) {
          rows.unshift({
            id: 'en-source',
            uri: this.editFocus.uri,
            target_lang: 'en',
            proposed_text: this.editFocus.source_text,
            source: 'ESCO_API',
            status: 'approved',
            confirmed_by: null
          })
        }
        this.editRows = this.sortEditRows(rows).concat(drafts)
      } catch (error) {
        this.noticeType = 'error'
        this.notice = error.message
        this.editRows = []
      } finally {
        this.editLoading = false
      }
    },
    async afterMutation() {
      await this.load(false)
      if (this.editOpen) await this.loadEditRows()
    },
    isEditable(item) {
      return item.status === 'pending' && (item.source === 'AI' || item.source === 'HUMAN')
    },
    canAct(item) {
      return this.isEditable(item)
    },
    canDeleteTranslation(row, index) {
      if (this.isDraft(row)) return false
      if (index === 0) return false
      if (!row || row.id === 'en-source') return false
      return String(row.target_lang || '').toLowerCase() !== 'en'
    },
    isDraft(row) {
      return !!(row && row.isDraft)
    },
    nextDraftId() {
      return 'draft-' + Date.now() + '-' + Math.floor(Math.random() * 1000)
    },
    selectedAddLang() {
      const lang = String(this.addLang || '').toLowerCase()
      if (lang && lang !== 'en') return lang
      return 'es'
    },
    pushDraft(partial) {
      this.editRows = this.editRows.concat([
        Object.assign(
          {
            id: this.nextDraftId(),
            isDraft: true,
            uri: this.editFocus && this.editFocus.uri,
            target_lang: this.selectedAddLang(),
            proposed_text: '',
            source: 'HUMAN',
            status: null,
            confirmed_by: null,
            model: '',
            source_text: this.editFocus && this.editFocus.source_text,
            escoOriginal: ''
          },
          partial
        )
      ])
    },
    addManualEntry() {
      this.pushDraft({
        source: 'HUMAN',
        draftKind: 'HUMAN',
        proposed_text: ''
      })
    },
    async addAiEntry() {
      if (!this.editFocus) return
      this.addBusy = 'AI'
      this.notice = ''
      try {
        const result = await this.api('backoffice/translations/preview-ai', 'POST', {
          sourceText: this.editFocus.source_text,
          sourceLang: 'en',
          targetLang: this.selectedAddLang()
        })
        this.pushDraft({
          source: 'AI',
          draftKind: 'AI',
          proposed_text: result.proposedText,
          model: result.model || ''
        })
      } catch (error) {
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.addBusy = ''
      }
    },
    async addEscoEntry() {
      if (!this.editFocus) return
      this.addBusy = 'ESCO'
      this.notice = ''
      try {
        const result = await this.api('backoffice/translations/preview-esco', 'POST', {
          uri: this.editFocus.uri,
          targetLang: this.selectedAddLang()
        })
        this.pushDraft({
          source: 'ESCO_API',
          draftKind: 'ESCO_API',
          proposed_text: result.proposedText,
          model: result.model || 'esco-api-v1.2.1',
          escoOriginal: result.proposedText
        })
      } catch (error) {
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.addBusy = ''
      }
    },
    onDraftTextInput(row) {
      if (!this.isDraft(row) || row.draftKind !== 'ESCO_API') return
      const current = String(row.proposed_text || '')
      row.source = current === String(row.escoOriginal || '') ? 'ESCO_API' : 'HUMAN'
    },
    removeDraft(row) {
      this.editRows = this.editRows.filter((item) => item.id !== row.id)
    },
    async saveDraft(row) {
      if (!this.isDraft(row)) return
      const text = String(row.proposed_text || '').trim()
      if (!text) {
        this.noticeType = 'error'
        this.notice = 'Texto vacío'
        return
      }
      this.busyId = row.id
      this.notice = ''
      try {
        await this.api('backoffice/translations/create', 'POST', {
          uri: row.uri,
          targetLang: row.target_lang,
          proposedText: text,
          source: row.source,
          sourceText: row.source_text,
          model: row.model
        })
        this.noticeType = 'success'
        this.notice = 'Translation saved.'
        this.removeDraft(row)
        await this.afterMutation()
      } catch (error) {
        if (error.message && error.message.includes('Authentication failed')) {
          this.$router.push('/auth-error')
          return
        }
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    },
    statusIcon(status) {
      if (status === 'approved') return 'mdi-check-circle'
      if (status === 'rejected') return 'mdi-close-circle'
      return 'mdi-clock-outline'
    },
    statusIconColor(status) {
      if (status === 'approved') return '#52ae32'
      if (status === 'rejected') return '#c62828'
      return '#757575'
    },
    statusHint(status) {
      if (status === 'approved') return 'Approved'
      if (status === 'rejected') return 'Rejected'
      if (status === 'pending') return 'Pending'
      return status || ''
    },
    sourceIcon(source) {
      if (source === 'ESCO_API') return 'mdi-earth'
      if (source === 'AI') return 'mdi-robot'
      if (source === 'HUMAN') return 'mdi-account'
      return 'mdi-help-circle-outline'
    },
    sourceIconColor(source) {
      if (source === 'ESCO_API') return '#047ec3'
      if (source === 'AI') return '#52ae32'
      if (source === 'HUMAN') return '#047ec3'
      return '#757575'
    },
    sourceHint(source) {
      if (source === 'ESCO_API') return 'Traducciones del API europeo ESCO'
      if (source === 'AI') return 'Traducciones hechas por IA'
      if (source === 'HUMAN') return 'Traducciones hechas por una persona'
      return source || ''
    },
    async api(path, method, body) {
      const options = {
        method: method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Auth-Token': this.$cookie.get('jwt_token')
        }
      }
      if (method === 'POST') options.body = JSON.stringify(body || {})
      let response = await fetch(process.env.VUE_APP_API_ENDPOINT + path, options)
      response = await response.json()
      if (!response.success) throw new Error(response.message || 'Error')
      return response.result
    },
    buildQuery() {
      const params = new URLSearchParams()
      params.set('limit', String(this.limit))
      params.set('offset', String(this.offset))
      if (this.filters.lang) params.set('lang', this.filters.lang)
      if (this.filters.status && this.filters.status !== 'all') {
        params.set('status', this.filters.status)
      }
      if (this.filters.source && this.filters.source !== 'all') {
        params.set('source', this.filters.source)
      }
      if (this.filters.type && this.filters.type !== 'all') {
        params.set('type', this.filters.type)
      }
      return params.toString()
    },
    onFilterChange() {
      this.offset = 0
      this.load(false)
    },
    prevPage() {
      this.offset = Math.max(0, this.offset - this.limit)
      this.load(false)
    },
    nextPage() {
      this.offset = this.offset + this.limit
      this.load(false)
    },
    async load(initial) {
      if (initial) this.loading = true
      else this.loadingPage = true
      this.notice = ''
      try {
        const result = await this.api('backoffice/translations?' + this.buildQuery())
        this.items = (result.items || []).map((item) => Object.assign({}, item))
        this.total = result.total || 0
        this.limit = result.limit || this.limit
        this.offset = result.offset != null ? result.offset : this.offset
        if (result.counters) {
          this.counters = {
            bySource: Object.assign(
              { ESCO_API: 0, AI: 0, HUMAN: 0 },
              result.counters.bySource || {}
            ),
            byStatus: Object.assign(
              { pending: 0, approved: 0, rejected: 0 },
              result.counters.byStatus || {}
            )
          }
        }
      } catch (error) {
        if (error.message && error.message.includes('Authentication failed')) {
          this.$router.push('/auth-error')
          return
        }
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.loading = false
        this.loadingPage = false
      }
    },
    async confirmItem(item) {
      this.busyId = item.id
      this.notice = ''
      try {
        await this.api('backoffice/translations/' + item.id + '/confirm', 'POST', {
          text: item.proposed_text
        })
        this.noticeType = 'success'
        this.notice = 'Translation confirmed.'
        await this.afterMutation()
      } catch (error) {
        if (error.message && error.message.includes('Authentication failed')) {
          this.$router.push('/auth-error')
          return
        }
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    },
    async rejectItem(item) {
      this.busyId = item.id
      this.notice = ''
      try {
        await this.api('backoffice/translations/' + item.id + '/reject', 'POST')
        this.noticeType = 'success'
        this.notice = 'Translation rejected.'
        await this.afterMutation()
      } catch (error) {
        if (error.message && error.message.includes('Authentication failed')) {
          this.$router.push('/auth-error')
          return
        }
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    },
    async deleteItem(item) {
      if (!item || item.id === 'en-source') return
      if (String(item.target_lang || '').toLowerCase() === 'en') return
      this.busyId = item.id
      this.notice = ''
      try {
        await this.api('backoffice/translations/' + item.id + '/delete', 'POST')
        this.noticeType = 'success'
        this.notice = 'Translation deleted.'
        await this.afterMutation()
      } catch (error) {
        if (error.message && error.message.includes('Authentication failed')) {
          this.$router.push('/auth-error')
          return
        }
        this.noticeType = 'error'
        this.notice = error.message
      } finally {
        this.busyId = null
      }
    }
  }
}
</script>

<style scoped>
.wrapper {
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 40px;
}
.toolbar-row {
  gap: 12px;
  padding-top: 10px;
  overflow: visible;
  justify-content: space-between;
  width: 100%;
}
.filters-row {
  flex: 0 0 auto;
}
.filter-select {
  max-width: 128px;
  min-width: 100px;
  flex: 0 0 128px;
}
.counters-row {
  gap: 6px;
  padding-bottom: 2px;
  margin-left: auto;
  flex: 0 0 auto;
}
.counter-chip {
  margin: 0;
  height: 28px !important;
  padding: 0 10px 0 10px !important;
  font-size: 0.8rem;
}
.counter-chip >>> .v-chip__content {
  padding: 0 2px 0 4px;
}
.counter-chip >>> .v-icon {
  margin-right: 4px;
  margin-left: 2px;
}
.background-gradient {
  color: white;
  box-shadow: 0 0 20px #eee;
  background-image: linear-gradient(to right, #047ec3 0%, #52ae32 200%);
}
.button-gradient-half {
  color: white;
  background-image: linear-gradient(to right, #047ec3 0%, #52ae32 100%);
}
.esco-link {
  color: #047ec3;
  font-size: 0.875rem;
  text-decoration: underline;
}
.translations-table >>> table {
  width: 100%;
  table-layout: fixed;
}
.uri-cell {
  white-space: nowrap;
  width: 96px;
}
.type-cell {
  white-space: nowrap;
  width: 90px;
}
.text-cell,
.proposed-cell {
  min-width: 0;
  white-space: normal;
  word-break: break-word;
}
.translations-table--dialog .proposed-cell {
  width: auto;
}
.proposed-cell >>> .v-textarea {
  margin: 0;
}
.translations-table td,
.translations-table th {
  vertical-align: middle;
}
.lang-cell,
.source-cell,
.status-cell {
  white-space: nowrap;
  text-align: center !important;
  width: 72px;
}
.actions-cell {
  white-space: nowrap;
  width: 56px;
}
.translations-table--dialog .actions-cell {
  width: 120px;
}
.add-row-bar {
  margin-bottom: 14px;
}
</style>
